/**
 * EINE MAIL AUS DER AKTE, MIT DATEIEN AUS DEM ORDNER (.332).
 *
 * ORIGINALTEXT MARVIN (07.10.2026)
 *   „ich will gerne aus der plattform, wenn ich in einem auftrag bin, zb eine
 *    email mit dateien, die bei uns in der struktur liegen, zusammenklicken
 *    können und dann direkt per mail zb an die telekom senden, ohne dass ich
 *    sie erst herunterladen muss und dann in eine email anhängen muss."
 *
 * Empfänger frei, dazu Vorschläge (Telekom-Postfach, Gemeinde, Firmen am
 * Auftrag). Die Dateien stehen nach Ordnern, zum Anhaken; die Summe wird
 * gezeigt und bei 20 MB wird gebremst. Gesendet wird vom Hauspostfach, mit
 * Blindkopie ans Backoffice; die Auftragsinfo trägt den Vermerk.
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api.ts';
import { Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage } from './Formular.tsx';

const mb = (b: number) => `${(b / 1024 / 1024).toFixed(b >= 10 * 1024 * 1024 ? 0 : 1)} MB`;

export function MailDialog({ auftragId, zu, fertig }: { auftragId: string; zu: () => void; fertig: () => void }) {
  const [d, setD] = useState<any>(null);
  const [an, setAn] = useState('');
  const [cc, setCc] = useState('');
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [ergebnis, setErgebnis] = useState<any>(null);

  useEffect(() => {
    api.akteMail(auftragId).then((v) => { setD(v); setBetreff(v.betreff ?? ''); }).catch((f) => setFehler(ausFehler(f)));
  }, [auftragId]);

  const ordner = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const x of d?.dateien ?? []) { if (!m.has(x.ordner)) m.set(x.ordner, []); m.get(x.ordner)!.push(x); }
    return [...m.entries()];
  }, [d]);
  const summe = (d?.dateien ?? []).filter((x: any) => gewaehlt.has(x.id)).reduce((s: number, x: any) => s + Number(x.groesse ?? 0), 0);
  const grenze = (d?.hoechstens_mb ?? 20) * 1024 * 1024;
  const zuViel = summe > grenze;

  const dazu = (mail: string) => {
    const alle = an.split(/[,;\s]+/).filter(Boolean);
    if (!alle.includes(mail)) setAn([...alle, mail].join(', '));
  };
  const umschalten = (id: string) => setGewaehlt((alt) => { const n = new Set(alt); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const ordnerUmschalten = (liste: any[]) => setGewaehlt((alt) => {
    const n = new Set(alt); const alleDrin = liste.every((x) => n.has(x.id));
    for (const x of liste) { if (alleDrin) n.delete(x.id); else n.add(x.id); }
    return n;
  });

  async function senden() {
    setLaeuft(true); setFehler(null);
    try {
      const r = await api.akteMailSenden(auftragId, { an, cc: cc || undefined, betreff, text, dateien: [...gewaehlt] });
      setErgebnis(r);
      if (r.gesendet) setTimeout(fertig, 1200);
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  return (
    <Dialog titel="Mail mit Dateien aus dem Ordner" breite={760} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {ergebnis?.gesendet && <div className="hinweis erledigt">Gesendet an {ergebnis.an.join(', ')}{ergebnis.anhaenge ? `, ${ergebnis.anhaenge} Anhang${ergebnis.anhaenge === 1 ? '' : 'änge'}` : ''}.</div>}
      {ergebnis && !ergebnis.gesendet && <div className="hinweis warnung">Nicht gesendet: {ergebnis.grund ?? ergebnis.fehler ?? 'der Mailweg hat abgelehnt'}.</div>}
      <Text name="an" titel="An" wert={an} setze={setAn} pflicht fehler={fehler}
            platzhalter="mehrere Adressen mit Komma" />
      {!!d?.vorschlaege?.length && (
        <div className="klein" style={{ margin: '-4px 0 10px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {d.vorschlaege.map((v: any) => (
            <button key={v.mail} type="button" className="knopf klein" title={v.mail} onClick={() => dazu(v.mail)}>
              + {v.name}
            </button>
          ))}
        </div>
      )}
      <Text name="cc" titel="Kopie an" wert={cc} setze={setCc} fehler={fehler} hinweis="Eine Blindkopie ans Backoffice geht immer mit." />
      <Text name="betreff" titel="Betreff" wert={betreff} setze={setBetreff} pflicht fehler={fehler} />
      <Text name="text" titel="Text" wert={text} setze={setText} mehrzeilig pflicht fehler={fehler} />

      <div className="feld">
        <label>Anhänge aus dem Auftragsordner
          <span className="klein leise">, gewählt {gewaehlt.size}, {mb(summe)} von höchstens {d?.hoechstens_mb ?? 20} MB</span>
        </label>
        {zuViel && <div className="hinweis warnung">Zusammen sind das {mb(summe)}. Mehr als {d?.hoechstens_mb ?? 20} MB nimmt kein Postfach an; bitte weniger wählen.</div>}
        {!d && <div className="leise klein">Lade die Dateien …</div>}
        {d && !ordner.length && <div className="leise klein">Im Ordner liegt keine Datei.</div>}
        {ordner.map(([name, liste]) => (
          <details key={name} open={liste.some((x) => gewaehlt.has(x.id)) || ordner.length <= 3}>
            <summary>
              <input type="checkbox" checked={liste.every((x) => gewaehlt.has(x.id))}
                     onClick={(e) => e.stopPropagation()} onChange={() => ordnerUmschalten(liste)} />{' '}
              <b>{name}</b> <span className="leise klein">({liste.length})</span>
            </summary>
            <ul className="klein" style={{ listStyle: 'none', margin: '4px 0 8px 22px', padding: 0 }}>
              {liste.map((x) => (
                <li key={x.id} style={{ margin: '2px 0' }}>
                  <label style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={gewaehlt.has(x.id)} onChange={() => umschalten(x.id)} />{' '}
                    {x.dateiname} <span className="leise">, {mb(Number(x.groesse ?? 0))}</span>
                  </label>
                </li>
              ))}
            </ul>
          </details>
        ))}
      </div>

      <div className="reihe" style={{ marginTop: 12 }}>
        <button type="button" className="knopf" onClick={zu} disabled={laeuft}>Schließen</button>
        <button type="button" className="knopf haupt" onClick={senden}
                disabled={laeuft || zuViel || !an.trim() || !betreff.trim() || !text.trim() || !!ergebnis?.gesendet}>
          {gewaehlt.size ? `Senden mit ${gewaehlt.size} Anhang${gewaehlt.size === 1 ? '' : 'änge'}` : 'Senden ohne Anhang'}
        </button>
      </div>
    </Dialog>
  );
}
